import { apiClient } from '../../services/apiClient';
import { brandConfig } from '../../config/brands';
import type {
  Organization,
  CreditsInfo,
  UsageLogsResponse,
  OrganizationsResponse,
  CreditRatesResponse,
  CreateJoinRequestData,
  CreateJoinRequestResponse,
  JoinRequestsResponse,
  ApproveJoinRequestData,
  ApproveJoinRequestResponse,
  RejectJoinRequestData,
  RejectJoinRequestResponse,
  CreateOrganizationData,
  CreateOrganizationResponse,
  SearchOrganizationsResponse,
  StorageInfo,
  TransactionsResponse,
  TransactionsParams,
  SkipOrganizationSetupResponse,
  UpdateOrganizationData,
} from './types';

/**
 * 自分が所属する組織の情報を取得
 */
export async function getMyOrganization(): Promise<Organization> {
  const response = await apiClient.get<Organization>('/organizations/me');
  return response.data;
}

/**
 * クレジット残高と取引履歴を取得
 */
export async function getCreditsInfo(): Promise<CreditsInfo> {
  const response = await apiClient.get<CreditsInfo>('/organizations/me/credits');
  return response.data;
}

/**
 * 使用量履歴を取得
 */
export async function getUsageLogs(params?: {
  start_date?: string;
  end_date?: string;
  operation_type?: string;
  limit?: number;
}): Promise<UsageLogsResponse> {
  const response = await apiClient.get<UsageLogsResponse>('/organizations/me/usage', {
    params,
  });
  return response.data;
}

// NOTE: addCredits (POST /organizations/me/credits) は 2026-07-29 のセキュリティ監査 C-1 で
// バックエンドのエンドポイントごと削除した。ポイント付与は Stripe Webhook が正本。

/**
 * 全組織一覧を取得（プラットフォーム管理者のみ）
 */
export async function listOrganizations(): Promise<OrganizationsResponse> {
  const response = await apiClient.get<OrganizationsResponse>('/organizations');
  return response.data;
}

/**
 * クレジットレート設定を取得
 */
export async function getCreditRates(): Promise<CreditRatesResponse> {
  const response = await apiClient.get<CreditRatesResponse>('/organizations/credit-rates');
  return response.data;
}

// Organization Join Requests APIs

/**
 * Slugで組織情報を取得（認証不要）
 */
export async function getOrganizationBySlug(slug: string): Promise<Organization> {
  const response = await apiClient.get<Organization>(`/organizations/slug/${slug}`);
  return response.data;
}

/**
 * 組織への参加依頼を作成
 */
export async function createJoinRequest(
  organizationId: string,
  data: CreateJoinRequestData
): Promise<CreateJoinRequestResponse> {
  const response = await apiClient.post<CreateJoinRequestResponse>(
    `/organizations/${organizationId}/join-requests`,
    data
  );
  return response.data;
}

/**
 * 組織の参加依頼一覧を取得（管理者のみ）
 */
export async function getJoinRequests(
  organizationId: string,
  status?: 'pending' | 'approved' | 'rejected'
): Promise<JoinRequestsResponse> {
  const response = await apiClient.get<JoinRequestsResponse>(
    `/organizations/${organizationId}/join-requests`,
    { params: status ? { status } : undefined }
  );
  return response.data;
}

/**
 * 参加依頼を承認（管理者のみ）
 */
export async function approveJoinRequest(
  organizationId: string,
  requestId: string,
  data: ApproveJoinRequestData
): Promise<ApproveJoinRequestResponse> {
  const response = await apiClient.post<ApproveJoinRequestResponse>(
    `/organizations/${organizationId}/join-requests/${requestId}/approve`,
    data
  );
  return response.data;
}

/**
 * 参加依頼を却下（管理者のみ）
 */
export async function rejectJoinRequest(
  organizationId: string,
  requestId: string,
  data: RejectJoinRequestData
): Promise<RejectJoinRequestResponse> {
  const response = await apiClient.post<RejectJoinRequestResponse>(
    `/organizations/${organizationId}/join-requests/${requestId}/reject`,
    data
  );
  return response.data;
}

/**
 * 新しい組織を作成
 */
export async function createOrganization(
  data: CreateOrganizationData
): Promise<CreateOrganizationResponse> {
  // partner_code を載せてログイン要求と partner 解決を一致させる。
  // localhost では複数 partner が Origin を共有するため、これが無いと
  // backend が誤った partner (例: default) に解決してしまう。
  const response = await apiClient.post<CreateOrganizationResponse>(
    '/organizations',
    { ...data, partner_code: brandConfig.id }
  );
  return response.data;
}

/**
 * 自分宛で組織作成を塞いでいる招待を辞退する（認証済み・トークン不要）
 *
 * 招待メールを紛失したユーザーの脱出路。メール経由の辞退
 * (/decline-invitation/:token) と違い、ログインさえできれば使える。
 */
export async function declineMyInvitation(
  invitationId: string
): Promise<{ success: boolean; status?: string; message?: string }> {
  const response = await apiClient.post<{ success: boolean; status?: string; message?: string }>(
    `/invitations/${invitationId}/decline`
  );
  return response.data;
}

/**
 * 同一ドメインの組織を検索（認証済みユーザーのみ）
 */
export async function searchOrganizations(
  query?: string
): Promise<SearchOrganizationsResponse> {
  const response = await apiClient.get<SearchOrganizationsResponse>(
    '/organizations/search',
    { params: query ? { query } : undefined }
  );
  return response.data;
}

/**
 * ストレージ使用量と上限を取得
 */
export async function getStorageInfo(): Promise<StorageInfo> {
  const response = await apiClient.get<StorageInfo>('/organizations/me/storage');
  return response.data;
}

/**
 * 取引履歴を取得（ページネーション対応）
 */
export async function getTransactions(params?: TransactionsParams): Promise<TransactionsResponse> {
  const response = await apiClient.get<TransactionsResponse>('/organizations/me/transactions', {
    params,
  });
  return response.data;
}

/**
 * 組織作成をスキップして個人ワークスペースを自動作成
 */
export async function skipOrganizationSetup(): Promise<SkipOrganizationSetupResponse> {
  // partner_code を載せてログイン要求と partner 解決を一致させる (localhost の Origin 共有対策)。
  const response = await apiClient.post<SkipOrganizationSetupResponse>(
    '/organizations/skip-setup',
    { partner_code: brandConfig.id }
  );
  return response.data;
}

/**
 * 組織情報を更新（管理者のみ）
 */
export async function updateMyOrganization(
  data: UpdateOrganizationData
): Promise<Organization> {
  const response = await apiClient.patch<Organization>('/organizations/me', data);
  return response.data;
}
